'use client'

import { useId, useRef } from 'react'
import Image from 'next/image'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery, Grid, Box, Typography } from '@mui/material'
import { dir } from 'i18next'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

// Types
import type { AwardsProps } from '@/types/components/molecules/awards'

// Helpers
import { useTranslation } from '@/helpers/i18n/client'

const AwardsMolecule = (props: AwardsProps) => {
	// Props
	const { lng } = props

	// Variables
	const lngDir = dir(lng)
	const { t } = useTranslation(lng)
	const muiTheme = useTheme()
	const pinkPatterId = useId()
	const pinkPatterBId = useId()
	const navyBluePatterId = useId()
	const gridRef = useRef<HTMLDivElement>(null)
	const pinkGridRef = useRef<HTMLDivElement>(null)
	const rockerRef = useRef<HTMLDivElement>(null)
	const navyBlueGridRef = useRef<HTMLDivElement>(null)
	const lessThanMedium = useMediaQuery(muiTheme.breakpoints.down('md'))
	const width = 100
	const height = 100
	const degree = 10

	// GSAP
	useGSAP(
		() => {
			gsap.timeline({
				scrollTrigger: {
					trigger: gridRef.current,
					start: 'top bottom',
					end: 'bottom top',
					// markers: true,
					onUpdate: self => {
						gsap.set(pinkGridRef.current, {
							rotateZ: lngDir === 'rtl' ? -2 * self.progress * degree : 2 * self.progress * degree - 10
						})

						gsap.set(rockerRef.current, {
							rotateZ: lngDir === 'rtl' ? self.progress * degree : -1 * self.progress * degree,
							y: self.progress * 100 - 50
						})

						gsap.set(navyBlueGridRef.current, {
							rotateZ: lngDir === 'rtl' ? -2 * self.progress * degree : 2 * self.progress * degree - 10
						})

						gsap.set('pattern', {
							attr: { x: `${self.progress * 100}%` }
						})
					}
				}
			})
		},
		{ scope: gridRef }
	)

	// Callbacks
	const generatePath = (padding: number) => {
		return `M ${padding} ${padding} ` + `L ${width - padding} ${padding} ` + `L ${width - padding} ${height - padding} ` + `L ${padding} ${height - padding} ` + `Z`
	}

	return (
		<Grid ref={gridRef} container height={{ xs: '200dvw', sm: '60dvw' }} position="relative">
			<Grid
				ref={pinkGridRef}
				size={{ xs: 12, sm: 7.2 }}
				height={{ xs: '100dvw', sm: '60dvw' }}
				position="absolute"
				top={0}
				left={{ xs: 0, sm: '-7.5dvw' }}
				zIndex={1}
				overflow="hidden"
				sx={{
					transform: 'rotateZ(-10deg)',
					filter: 'drop-shadow(0px 4px 152.5px rgba(255, 145, 145, 0.19))'
				}}
			>
				<svg viewBox={`0 0 ${width} ${height}`}>
					<defs>
						<pattern id={pinkPatterId} patternUnits="userSpaceOnUse" width={9} height={7.5} patternTransform="rotate(45)">
							<line stroke="#ff8484" strokeWidth={7.5} x1={0} y={0} x2={0} y2={7.5} />
						</pattern>
					</defs>

					<path fill="#ff9191" d={generatePath(0)} />
					<path fill={`url(#${pinkPatterId})`} fillOpacity={0.55} d={generatePath(lessThanMedium ? 5 : 2.5)} />
				</svg>

				<Box
					width="30%"
					height="60%"
					position="absolute"
					top="35%"
					sx={{
						...(lngDir === 'rtl' ? { right: '80%' } : { left: '80%' }),
						transform: `translate(${lngDir === 'rtl' ? '50%' : '-50%'}, -50%)`
					}}
				>
					<Image src="/images/items/stones.png" alt="stones" fill={true} style={{ objectFit: 'cover' }} />
				</Box>

				<Box
					ref={rockerRef}
					width="60%"
					height="60%"
					position="absolute"
					top="45%"
					sx={{
						...(lngDir === 'rtl' ? { right: '50%' } : { left: '50%' }),
						transform: `translate(${lngDir === 'rtl' ? '50%' : '-50%'}, -50%)`,
						filter: 'drop-shadow(0px 4px 152.5px rgba(255, 255, 255, 0.25))'
					}}
				>
					<Image src="/images/items/rocket.png" alt="rocket" fill={true} style={{ objectFit: 'cover' }} />
				</Box>

				<Box
					width="120%"
					height="120%"
					position="absolute"
					top="120%"
					sx={{
						...(lngDir === 'rtl' ? { right: '40%' } : { left: '40%' }),
						transform: `translate(${lngDir === 'rtl' ? '50%' : '-50%'}, -50%) rotateZ(${lngDir === 'rtl' ? '-45.76deg' : '45.76deg'})`,
						filter: 'drop-shadow(0px 4px 152.5px rgba(255, 145, 145, 0.19))'
					}}
				>
					<Image src="/images/items/circle.png" alt="awards" fill={true} style={{ objectFit: 'cover' }} />
				</Box>

				<Box
					width={{ xs: '40%', md: '30%' }}
					position="absolute"
					top={{ xs: '35%', md: '25%' }}
					sx={{
						...(lngDir === 'rtl' ? { right: '80%' } : { left: '80%' }),
						transform: `translate(${lngDir === 'rtl' ? '50%' : '-50%'}, -50%)`
					}}
				>
					<svg viewBox="0 0 100 38.9">
						<path
							fill="#fff"
							d="M 0 14.2 C 0 10.5 2.8 7.5 6.4 7.1 L 89.5 0 C 93.3 -0.3 96.7 2.4 97.1 6.2 L 100 31 C 100.5 35.2 97.2 38.9 93 38.9 H 7.1 C 3.2 38.9 0 35.7 0 31.8 V 14.2 Z"
						/>
					</svg>

					<Box
						display="flex"
						flexDirection="column"
						justifyContent="center"
						width={1}
						height={1}
						py={2}
						position="absolute"
						top={0}
						left={0}
						color="common.black"
						zIndex={1}
						sx={{
							...(lngDir === 'rtl' ? { pr: 8, pl: 2 } : { pr: 2, pl: 8 })
						}}
					>
						<Box
							position="absolute"
							top="55%"
							fontSize={{ xs: '120%', lg: '200%' }}
							sx={{
								...(lngDir === 'rtl' ? { right: 32 } : { left: 32 }),
								transform: `translate(${lngDir === 'rtl' ? '50%' : '-50%'}, -50%)`
							}}
						>
							🏆
						</Box>

						<Typography component="div" fontSize={{ xs: 14, sm: 12, lg: 20 }} fontWeight={600} lineHeight={1.14}>
							{t('common:title.3dLicence')}
						</Typography>

						<Typography component="div" mt={0.5} fontSize={{ xs: 10, sm: 9, lg: 14 }} fontWeight={500} lineHeight={1.14}>
							{t('common:title.regitiVoice')}
						</Typography>
					</Box>
				</Box>

				<Box
					width={{ xs: '40%', md: '30%' }}
					position="absolute"
					top={{ xs: '65%', md: '65%' }}
					sx={{
						...(lngDir === 'rtl' ? { left: '60%' } : { right: '60%' }),
						transform: `translate(${lngDir === 'rtl' ? '-50%' : '50%'}, -50%)`
					}}
				>
					<svg viewBox="0 0 100 38.9">
						<path
							fill="#fff"
							d="M 100 14.2 C 100 10.5 97.2 7.5 93.6 7.1 L 10.5 0 C 6.7 -0.3 3.3 2.4 2.9 6.2 L 0 31 C -0.5 35.2 2.8 38.9 7 38.9 H 92.9 C 96.8 38.9 100 35.7 100 31.8 V 14.2 Z"
						/>
					</svg>

					<Box
						display="flex"
						flexDirection="column"
						justifyContent="center"
						width={1}
						height={1}
						py={2}
						position="absolute"
						top={0}
						left={0}
						color="common.black"
						zIndex={1}
						sx={{
							...(lngDir === 'rtl' ? { pr: 8, pl: 2 } : { pr: 2, pl: 8 })
						}}
					>
						<Box
							position="absolute"
							top="55%"
							fontSize={{ xs: '120%', lg: '200%' }}
							sx={{
								...(lngDir === 'rtl' ? { right: 32 } : { left: 32 }),
								transform: `translate(${lngDir === 'rtl' ? '50%' : '-50%'}, -50%)`
							}}
						>
							🚀
						</Box>

						<Typography component="div" fontSize={{ xs: 14, sm: 12, lg: 20 }} fontWeight={600} lineHeight={1.14}>
							{t('common:title.bestAgency')}
						</Typography>

						<Typography component="div" mt={0.5} fontSize={{ xs: 10, sm: 9, lg: 14 }} fontWeight={500} lineHeight={1.14}>
							{t('common:title.theAwards', { year: '2023' })}
						</Typography>
					</Box>
				</Box>
			</Grid>

			<Grid
				ref={navyBlueGridRef}
				size={{ xs: 12, sm: 7.2 }}
				height={{ xs: '100dvw', sm: '60dvw' }}
				position="absolute"
				top={{ xs: '90dvw', sm: 0 }}
				right={{ xs: 0, sm: '-7.5dvw' }}
				sx={{ transform: 'rotateZ(-10deg)' }}
			>
				<svg viewBox={`0 0 ${width} ${height}`}>
					<defs>
						<pattern id={navyBluePatterId} patternUnits="userSpaceOnUse" width={9} height={7.5} patternTransform="rotate(45)">
							<line stroke="#2e3a64" strokeWidth={7.5} x1={0} y={0} x2={0} y2={7.5} />
						</pattern>
					</defs>

					<path fill="#192448" d={generatePath(0)} />
					<path fill={`url(#${navyBluePatterId})`} fillOpacity={0.55} d={generatePath(lessThanMedium ? 5 : 2.5)} />
				</svg>

				<Box width={1} height={1} position="absolute" top={0} left={0}>
					<Grid container flexDirection="column" justifyContent="center" alignItems="center" columns={12} spacing={2} height={1} p={{ xs: 6, md: 12 }}>
						<Grid size={12}>
							<Box width={1}>
								<Grid container justifyContent="center" alignItems="center" spacing={2}>
									<Grid size="auto">
										<Typography component="div" fontSize={{ xs: 35, md: 50, lg: 70 }} fontWeight={900} lineHeight={1.7}>
											+4
										</Typography>

										<Typography component="div" fontSize={{ xs: 14, md: 18, lg: 23 }} fontWeight={600} lineHeight={1.7}>
											{t('common:title.happyClients')}
										</Typography>
									</Grid>

									<Grid size="auto">
										<Typography component="div" fontSize={{ xs: 35, md: 50, lg: 70 }} fontWeight={900} lineHeight={1.7}>
											+7
										</Typography>

										<Typography component="div" fontSize={{ xs: 14, md: 18, lg: 23 }} fontWeight={600} lineHeight={1.7}>
											{t('common:title.successfullProjects')}
										</Typography>
									</Grid>
								</Grid>
							</Box>
						</Grid>

						<Grid size={10}>
							<Box width={1} position="relative">
								<svg viewBox="0 0 100 20.3032">
									<path
										fill="#fff"
										d="M 0.1673 6.7121 C 0.0558 4.2983 0 3.0914 0.4586 2.1748 C 0.8125 1.4673 1.3716 0.8831 2.0628 0.4984 C 2.9584 0 4.1666 0.0027 6.583 0.008 L 98.2423 0.0693 C 100 0.0682 100.7611 2.2946 99.3703 3.3697 C 97.9225 4.4889 97.1108 6.2435 97.1953 8.0717 L 97.2939 10.2058 L 97.4506 13.595 C 97.562 16.0065 97.6176 17.2123 97.1597 18.1284 C 96.8063 18.8356 96.2479 19.4197 95.5574 19.8046 C 94.6628 20.3032 93.4558 20.302 91.0416 20.2991 L 6.9024 20.2023 C 4.6862 20.1998 3.5782 20.1986 2.7255 19.7535 C 2.0662 19.4094 1.5181 18.8855 1.1447 18.2423 C 0.6617 17.4104 0.6105 16.3036 0.5082 14.0899 L 0.1673 6.7121 Z"
									/>
								</svg>

								<Typography
									component="div"
									width={1}
									position="absolute"
									top="50%"
									pl={2}
									pr={4}
									fontSize={{ xs: 12, md: 14, lg: 18 }}
									fontWeight={900}
									lineHeight={{ xs: 1, md: 1.3, lg: 1.7 }}
									textAlign="center"
									color="common.black"
									sx={{
										...(lngDir === 'rtl' ? { right: '50%' } : { left: '50%' }),
										transform: `translate(${lngDir === 'rtl' ? '50%' : '-50%'}, -50%)`
									}}
								>
									{t('common:description.idea')}
								</Typography>
							</Box>
						</Grid>

						<Grid size={10}>
							<Box width={1} position="relative">
								<svg viewBox="0 0 100 41.1">
									<defs>
										<pattern id={pinkPatterBId} patternUnits="userSpaceOnUse" width={9} height={7.5} patternTransform="rotate(45)">
											<line stroke="#ff8484" strokeWidth={7.5} x1={0} y={0} x2={0} y2={7.5} />
										</pattern>
									</defs>

									<path
										fill="#ff9191"
										d="M 1.2 3.8 C -0.7 2.8 0 0 2.1 0 L 92.9 0 C 95.2 0 96.3 0 97.2 0.5 C 97.9 0.8 98.4 1.4 98.8 2 C 99.3 2.9 99.3 4 99.4 6.3 L 100 34.5 C 100.1 36.9 100.1 38.1 99.6 38.9 C 99.2 39.6 98.7 40.2 98 40.6 C 97.1 41.1 95.9 41.1 93.6 41.1 L 11 41.1 C 8.7 41.1 7.6 41.1 6.7 40.6 C 6 40.3 5.5 39.7 5.1 39 C 4.6 38.2 4.6 37 4.5 34.8 L 4.3 20.5 L 4.1 10.3 L 4.1 8.3 C 4 6.3 2.9 4.6 1.2 3.8 Z"
									/>

									<path
										fill={`url(#${pinkPatterBId})`}
										fillOpacity={0.55}
										d="M 3.64 4.86 C 1.835 3.91 2.5 1.25 4.495 1.25 L 90.755 1.25 C 92.94 1.25 93.985 1.25 94.84 1.725 C 95.505 2.01 95.98 2.58 96.36 3.15 C 96.835 4.005 96.835 5.05 96.93 7.235 L 97.5 34.025 C 97.595 36.305 97.595 37.445 97.12 38.205 C 96.74 38.87 96.265 39.44 95.6 39.82 C 94.745 40.295 93.605 40.295 91.42 40.295 L 12.95 40.295 C 10.765 40.295 9.72 40.295 8.865 39.82 C 8.2 39.535 7.725 38.965 7.345 38.3 C 6.87 37.54 6.87 36.4 6.775 34.31 L 6.585 20.725 L 6.395 11.035 L 6.395 9.135 C 6.3 7.235 5.255 5.62 3.64 4.86 Z"
									/>
								</svg>

								<Typography
									component="div"
									width={1}
									position="absolute"
									top="50%"
									pl={4}
									pr={2}
									fontSize={{ xs: 12, md: 14, lg: 18 }}
									fontWeight={900}
									lineHeight={{ xs: 1, md: 1.3, lg: 1.7 }}
									textAlign="center"
									color="common.white"
									sx={{
										...(lngDir === 'rtl' ? { right: '50%' } : { left: '50%' }),
										transform: `translate(${lngDir === 'rtl' ? '50%' : '-50%'}, -50%)`
									}}
								>
									{t('common:description.solution')}
								</Typography>
							</Box>
						</Grid>
					</Grid>
				</Box>
			</Grid>
		</Grid>
	)
}

export default AwardsMolecule
